import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { Link } from "react-router-dom";
import AuthApi from "../utils/authApi";

const RecoverAccount = () => {
  const { requestAccountRecovery, verifyAccountRecovery } = AuthApi();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");

  const [step, setStep] = useState("email");

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleRequestRecovery = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError("Please enter your email address.");
      return;
    }

    setLoading(true);

    try {
      await requestAccountRecovery(normalizedEmail);

      setStep("otp");

      setSuccess(
        "A recovery verification code has been sent to your email address.",
      );
    } catch (error) {
      setError(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong while requesting account recovery.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverAccount = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    const normalizedEmail = email.trim().toLowerCase();
    const normalizedOtp = otp.trim();

    if (!normalizedEmail) {
      setError("Please enter your email address.");
      return;
    }

    if (!normalizedOtp) {
      setError("Please enter the recovery verification code.");
      return;
    }

    if (!/^\d{6}$/.test(normalizedOtp)) {
      setError("Please enter the 6-digit recovery code.");
      return;
    }

    setLoading(true);

    try {
      await verifyAccountRecovery(normalizedEmail, normalizedOtp);

      setStep("success");

      setSuccess(
        "Your account has been successfully recovered. You can now sign in.",
      );
    } catch (error) {
      setError(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong while recovering your account.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setStep("email");
    setOtp("");
    setError("");
    setSuccess("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f7f9fc",
        py: { xs: 4, md: 7 },
      }}
    >
      <Container maxWidth="sm">
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 5 }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#1D629D",
            }}
          >
            PristinePV
          </Typography>

          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: "none",
              color: "#1D629D",
              fontWeight: 600,
            }}
          >
            Back to website
          </Button>
        </Stack>

        {/* Card */}
        <Card
          elevation={0}
          sx={{
            border: "1px solid #e3e8ef",
            borderRadius: 3,
            backgroundColor: "#ffffff",
          }}
        >
          <CardContent
            sx={{
              p: {
                xs: 3,
                md: 5,
              },
            }}
          >
            {/* Page Header */}
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#eaf4f9",
                }}
              >
                {step === "success" ? (
                  <VerifiedOutlinedIcon
                    sx={{
                      fontSize: 34,
                      color: "#1D629D",
                    }}
                  />
                ) : (
                  <RestoreIcon
                    sx={{
                      fontSize: 34,
                      color: "#1D629D",
                    }}
                  />
                )}
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontSize: {
                    xs: "28px",
                    md: "36px",
                  },
                  fontWeight: 700,
                  color: "#1f2937",
                }}
              >
                {step === "success"
                  ? "Account recovered"
                  : "Recover your account"}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#5f6b7a",
                  lineHeight: 1.7,
                  maxWidth: 500,
                }}
              >
                {step === "success"
                  ? "Your PristinePV account has been successfully restored."
                  : "If your PristinePV account was deleted within the last 30 days, you can recover it using your email address."}
              </Typography>
            </Stack>

            <Divider sx={{ my: 4 }} />

            {/* Messages */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                }}
              >
                {error}
              </Alert>
            )}

            {success && step !== "success" && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                }}
              >
                {success}
              </Alert>
            )}

            {/* Step 1 */}
            {step === "email" && (
              <Box component="form" onSubmit={handleRequestRecovery}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Email address"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Enter your PristinePV email"
                    autoComplete="email"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <EmailOutlinedIcon
                          sx={{
                            mr: 1,
                            color: "#7a8594",
                          }}
                        />
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    startIcon={<RestoreIcon />}
                    sx={{
                      py: 1.4,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "16px",
                      backgroundColor: "#1D629D",
                      "&:hover": {
                        backgroundColor: "#174f7d",
                      },
                    }}
                  >
                    {loading
                      ? "Sending recovery code..."
                      : "Send recovery code"}
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Step 2 */}
            {step === "otp" && (
              <Box component="form" onSubmit={handleRecoverAccount}>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#667382",
                        mb: 1,
                      }}
                    >
                      Recovery code sent to
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#273444",
                      }}
                    >
                      {email}
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="Recovery verification code"
                    value={otp}
                    onChange={(event) => {
                      const value = event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);

                      setOtp(value);
                    }}
                    placeholder="Enter 6-digit code"
                    inputProps={{
                      maxLength: 6,
                      inputMode: "numeric",
                    }}
                    disabled={loading}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    startIcon={<VerifiedOutlinedIcon />}
                    sx={{
                      py: 1.4,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "16px",
                      backgroundColor: "#1D629D",
                      "&:hover": {
                        backgroundColor: "#174f7d",
                      },
                    }}
                  >
                    {loading ? "Recovering account..." : "Recover account"}
                  </Button>

                  <Button
                    type="button"
                    variant="text"
                    onClick={handleChangeEmail}
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      color: "#1D629D",
                      fontWeight: 600,
                    }}
                  >
                    Use a different email address
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Success */}
            {step === "success" && (
              <Stack spacing={3} alignItems="center" textAlign="center">
                <Alert
                  severity="success"
                  sx={{
                    width: "100%",
                    borderRadius: 2,
                  }}
                >
                  {success}
                </Alert>

                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: 520,
                    color: "#4b5563",
                    lineHeight: 1.7,
                  }}
                >
                  Your PristinePV account has been successfully recovered.
                  Please return to the PristinePV mobile app and sign in using
                  your existing email address and password.
                </Typography>

                <Button
                  component={Link}
                  to="/"
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 5,
                    py: 1.4,
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#1D629D",
                    borderColor: "#1D629D",
                    "&:hover": {
                      borderColor: "#174f7d",
                      backgroundColor: "#f0f7fb",
                    },
                  }}
                >
                  Return to PristinePV
                </Button>
              </Stack>
            )}

            <Divider sx={{ my: 4 }} />

            {/* Information */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#273444",
                  mb: 1,
                }}
              >
                Important
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#667382",
                  lineHeight: 1.7,
                }}
              >
                Account recovery is only available during the 30-day recovery
                period following account deletion. After this period, the
                account cannot be recovered.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Legal links */}
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
            >
              <Button
                component={Link}
                to="/privacy-policy"
                sx={{
                  textTransform: "none",
                  color: "#1D629D",
                  fontWeight: 600,
                }}
              >
                Privacy Policy
              </Button>

              <Button
                component={Link}
                to="/terms-and-conditions"
                sx={{
                  textTransform: "none",
                  color: "#1D629D",
                  fontWeight: 600,
                }}
              >
                Terms & Conditions
              </Button>

              <Button
                component={Link}
                to="/delete-account"
                sx={{
                  textTransform: "none",
                  color: "#1D629D",
                  fontWeight: 600,
                }}
              >
                Delete Account
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Footer */}
        <Typography
          variant="body2"
          align="center"
          sx={{
            mt: 4,
            color: "#7a8594",
          }}
        >
          © {new Date().getFullYear()} Pristine PV Limited. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default RecoverAccount;
